import { ViewStyle, TextStyle, ImageStyle } from 'react-native';

// All possible React Native style properties
const RN_STYLE_PROPS = new Set([
  // Layout
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'marginHorizontal',
  'marginVertical',
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'paddingHorizontal',
  'paddingVertical',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'zIndex',
  // Flexbox
  'flex',
  'flexBasis',
  'flexDirection',
  'flexGrow',
  'flexShrink',
  'flexWrap',
  'alignContent',
  'alignItems',
  'alignSelf',
  'justifyContent',
  'aspectRatio',
  // Visual
  'backgroundColor',
  'borderColor',
  'borderTopColor',
  'borderRightColor',
  'borderBottomColor',
  'borderLeftColor',
  'borderRadius',
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderBottomLeftRadius',
  'borderBottomRightRadius',
  'borderStyle',
  'borderWidth',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'opacity',
  'elevation',
  'shadowColor',
  'shadowOffset',
  'shadowOpacity',
  'shadowRadius',
  // Text
  'color',
  'fontFamily',
  'fontSize',
  'fontStyle',
  'fontWeight',
  'letterSpacing',
  'lineHeight',
  'textAlign',
  'textAlignVertical',
  'textDecorationLine',
  'textDecorationStyle',
  'textDecorationColor',
  'textShadowColor',
  'textShadowOffset',
  'textShadowRadius',
  'textTransform',
  // Image
  'resizeMode',
  'overlayColor',
  'tintColor',
  // Transform
  'transform',
  'transformMatrix',
  'rotation',
  'scaleX',
  'scaleY',
  'translateX',
  'translateY',
  'skewX',
  'skewY',
  'perspective',
]);

type AllStyles = ViewStyle & TextStyle & ImageStyle;

interface ProcessedStyles {
  finalStyle: Record<string, any>;
  componentProps: Record<string, any>;
}

/**
 * Simple utility to merge styles with proper priority: style > attributes > config defaults
 * @param props - Component props
 * @returns Processed styles and component props
 */
export function processStyles(props: {
  attributes?: Record<string, any>;
  style?: Record<string, any>;
  config?: {
    style?: Record<string, any>;
    styleAttributes?: string[]; // List of attributes that should be treated as styles
  };
  libraryComponentManifest?: {
    style?: Record<string, any>;
    styleAttributes?: string[];
  };
  [key: string]: any;
}): ProcessedStyles {
  const {
    attributes = {},
    style = {},
    config,
    libraryComponentManifest,
    ...otherProps
  } = props;

  const finalStyle: Record<string, any> = {};
  const componentProps: Record<string, any> = { ...otherProps };

  // Use config from libraryComponentManifest if available, otherwise use config prop
  const activeConfig = libraryComponentManifest || config;
  // 1. Start with config defaults (lowest priority)
  if (activeConfig?.style) {
    Object.entries(activeConfig.style).forEach(([key, value]) => {
      // Handle nested style objects in manifest (like opacity: { min: 0, max: 1, step: 0.01 })
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        // Skip complex objects, only use primitive values as defaults
        if ('min' in value || 'max' in value || 'step' in value) {
          // Skip control objects
          return;
        }
      }

      if (
        RN_STYLE_PROPS.has(key) &&
        (typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean')
      ) {
        finalStyle[key] = value;
      }
    });
  }

  // 2. Apply attributes (medium priority)
  Object.entries(attributes).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      // Check if it's a React Native style prop OR defined as style attribute in config
      if (
        RN_STYLE_PROPS.has(key) ||
        activeConfig?.styleAttributes?.includes(key)
      ) {
        finalStyle[key] = value;
      } else {
        componentProps[key] = value;
      }
    }
  });

  // 3. Apply style prop (highest priority) - THIS SHOULD OVERRIDE EVERYTHING
  Object.entries(style).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      const previousValue = finalStyle[key];
      finalStyle[key] = value;
    }
  });
  return {
    finalStyle,
    componentProps,
  };
}

/**
 * Check if a property name is a React Native style property
 */
export function isStyleProp(propName: string): boolean {
  return RN_STYLE_PROPS.has(propName);
}

export function mergeStyles(
  ...styles: Array<Record<string, any> | null | undefined>
): Record<string, any> {
  return styles.reduce<Record<string, any>>((merged, styleObj) => {
    if (styleObj && typeof styleObj === 'object') {
      return { ...merged, ...styleObj };
    }
    return merged;
  }, {});
}

/**
 * Simple utility to handle dimension values that could be numbers or percentage strings
 * @param value - The dimension value (number, "100%", etc.)
 * @returns The value properly formatted for React Native styles
 */
export function parseDimension(value: any): number | string {
  // First, check for null/undefined
  if (value === null || value === undefined) {
    return 0;
  }

  // Check if it's actually a number (and not NaN)
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }

  // Check if it's actually a string
  if (typeof value === 'string') {
    // Trim any whitespace
    const trimmed = value.trim();

    // If empty string, return 0
    if (trimmed === '') {
      return 0;
    }

    // Check for percentage - use endsWith for better accuracy
    if (trimmed.endsWith('%')) {
      return trimmed;
    }

    // Try to parse as number
    const parsed = parseFloat(trimmed);
    return isNaN(parsed) ? 0 : parsed;
  }

  // For any other type, safely convert to string first, then handle
  try {
    const stringValue = String(value).trim();

    // Check for percentage in converted string
    if (stringValue.endsWith('%')) {
      return stringValue;
    }

    // Try to parse as number
    const parsed = parseFloat(stringValue);
    return isNaN(parsed) ? 0 : parsed;
  } catch (error) {
    // If any error occurs in conversion, return 0
    return 0;
  }
}

/**
 * Helper function to map common invalid icon names to valid icons
 * @param iconName - The icon name to validate/map
 * @param isCanvasMode - Whether component is in canvas/editor mode
 * @param context - Optional context to determine which icon library is being used
 * @returns Valid icon name
 */
export function getValidIconName(
  iconName: string,
  isCanvasMode: boolean = false,
  context?: string
): string {
  // In canvas mode, return original icon names for editor compatibility
  if (isCanvasMode) {
    return iconName;
  }
  const materialIconsMap: { [key: string]: string } = {
    account: 'account-circle',
    bell: 'notifications',
    cogs: 'settings',
    cog: 'settings',
    'help-circle': 'help',
    logout: 'logout',
    person: 'account-circle',
    notifications: 'notifications',
    home: 'home',
    'format-list-bulleted': 'format-list-bulleted',
    star: 'star',
    settings: 'settings',
  };

  const paperIconsMap: { [key: string]: string } = {
    account: 'account-circle',
    bell: 'bell',
    cogs: 'cog',
    cog: 'cog',
    'help-circle': 'help-circle',
    logout: 'logout',
    person: 'account-circle',
    notifications: 'bell',
    home: 'home',
    'format-list-bulleted': 'format-list-bulleted',
    star: 'star',
    settings: 'cog',
  };

  // Use appropriate mapping based on context
  const iconMap = context === 'paper' ? paperIconsMap : materialIconsMap;
  return iconMap[iconName] || iconName;
}
